/**
 * Shared (non-organ) procedural textures: pipes, molecules, port-base, port-cone,
 * castellated joint pieces, burst-X, and small drawing helpers. New organ art
 * lives in its own file (`./brain.ts`, `./stomach.ts`, etc.) so multiple agents
 * can add organs in parallel without colliding here.
 */

import Phaser from 'phaser';
import { GRID_SIZE } from '../../grid';
import { COLORS } from '../../colors';

export type HasTexture = (key: string) => boolean;

export function registerCoreTextures(scene: Phaser.Scene, has: HasTexture): void {
  if (!has('pipe-straight-idle'))
    makePipeStraight(
      scene,
      'pipe-straight-idle',
      COLORS.idleFill,
      COLORS.idleOutline,
      COLORS.idleHighlight,
    );
  if (!has('pipe-straight-flow'))
    makePipeStraight(
      scene,
      'pipe-straight-flow',
      COLORS.flowFill,
      COLORS.flowOutline,
      COLORS.flowHighlight,
    );
  if (!has('pipe-straight-flow-gruel'))
    makePipeStraight(
      scene,
      'pipe-straight-flow-gruel',
      COLORS.flowFillGruel,
      COLORS.flowOutlineGruel,
      COLORS.flowHighlightGruel,
    );
  if (!has('pipe-elbow-idle'))
    makePipeElbow(
      scene,
      'pipe-elbow-idle',
      COLORS.idleFill,
      COLORS.idleOutline,
      COLORS.idleHighlight,
    );
  if (!has('pipe-elbow-flow'))
    makePipeElbow(
      scene,
      'pipe-elbow-flow',
      COLORS.flowFill,
      COLORS.flowOutline,
      COLORS.flowHighlight,
    );
  if (!has('pipe-elbow-flow-gruel'))
    makePipeElbow(
      scene,
      'pipe-elbow-flow-gruel',
      COLORS.flowFillGruel,
      COLORS.flowOutlineGruel,
      COLORS.flowHighlightGruel,
    );
  if (!has('mol-food')) makeMolFood(scene, 'mol-food');
  if (!has('mol-oxygen')) makeMolOxygen(scene, 'mol-oxygen');
  if (!has('mol-co2')) makeMolCo2(scene, 'mol-co2');
  if (!has('mol-gruel')) makeMolGruel(scene, 'mol-gruel');
  if (!has('mol-waste')) makeMolWaste(scene, 'mol-waste');
  if (!has('mol-pulse')) makeMolPulse(scene, 'mol-pulse');
  if (!has('mol-blood')) makeMolBlood(scene, 'mol-blood');
  if (!has('port-base')) makePortBase(scene, 'port-base');
  if (!has('port-cone')) makePortCone(scene, 'port-cone');
  if (!has('burst-x')) makeBurstX(scene, 'burst-x');
  if (!has('port-castellated-spout')) makeCastellatedSpout(scene, 'port-castellated-spout');
  if (!has('port-castellated-receptacle'))
    makeCastellatedReceptacle(scene, 'port-castellated-receptacle');
}

// --- Pipes -----------------------------------------------------------------

function makePipeStraight(
  scene: Phaser.Scene,
  key: string,
  fill: number,
  outline: number,
  highlight: number,
): void {
  const w = GRID_SIZE;
  const h = GRID_SIZE;
  const bodyH = 34;
  const bodyY = (h - bodyH) / 2;
  const g = scene.add.graphics();
  g.fillStyle(fill, 1);
  g.fillRect(0, bodyY, w, bodyH);
  g.lineStyle(2, outline, 1);
  g.strokeLineShape(new Phaser.Geom.Line(0, bodyY, w, bodyY));
  g.strokeLineShape(new Phaser.Geom.Line(0, bodyY + bodyH, w, bodyY + bodyH));
  g.fillStyle(outline, 0.7);
  g.fillRect(0, bodyY, 2, bodyH);
  g.fillRect(w - 2, bodyY, 2, bodyH);
  g.fillStyle(highlight, 0.7);
  g.fillRect(2, bodyY + 5, w - 4, 4);
  g.fillStyle(outline, 0.9);
  g.fillCircle(8, bodyY + bodyH - 5, 1.5);
  g.fillCircle(w - 8, bodyY + bodyH - 5, 1.5);
  g.generateTexture(key, w, h);
  g.destroy();
}

function makePipeElbow(
  scene: Phaser.Scene,
  key: string,
  fill: number,
  outline: number,
  highlight: number,
): void {
  const w = GRID_SIZE;
  const h = GRID_SIZE;
  const bodyW = 34;
  const cx = w / 2;
  const cy = h / 2;
  const g = scene.add.graphics();
  g.fillStyle(fill, 1);
  g.fillRect(cx - bodyW / 2, cy - bodyW / 2, bodyW, h - (cy - bodyW / 2));
  g.fillRect(cx - bodyW / 2, cy - bodyW / 2, w - (cx - bodyW / 2), bodyW);
  g.lineStyle(2, outline, 1);
  g.strokeLineShape(new Phaser.Geom.Line(cx - bodyW / 2, cy - bodyW / 2, w, cy - bodyW / 2));
  g.strokeLineShape(new Phaser.Geom.Line(cx - bodyW / 2, cy - bodyW / 2, cx - bodyW / 2, h));
  g.strokeLineShape(new Phaser.Geom.Line(cx + bodyW / 2, cy + bodyW / 2, w, cy + bodyW / 2));
  g.strokeLineShape(new Phaser.Geom.Line(cx + bodyW / 2, cy + bodyW / 2, cx + bodyW / 2, h));
  g.fillStyle(outline, 0.7);
  g.fillRect(w - 2, cy - bodyW / 2, 2, bodyW);
  g.fillRect(cx - bodyW / 2, h - 2, bodyW, 2);
  g.fillStyle(highlight, 0.6);
  g.fillRect(cx - bodyW / 2 + 4, cy - bodyW / 2 + 5, w - (cx - bodyW / 2) - 8, 3);
  g.fillRect(cx - bodyW / 2 + 5, cy - bodyW / 2 + 4, 3, h - (cy - bodyW / 2) - 8);
  g.generateTexture(key, w, h);
  g.destroy();
}

// --- Molecules -------------------------------------------------------------

function makeMolFood(scene: Phaser.Scene, key: string): void {
  const size = 22;
  const g = scene.add.graphics();
  g.fillStyle(COLORS.molFood, 1);
  g.lineStyle(2, COLORS.molFoodOutline, 1);
  drawPolygon(g, size / 2, size / 2, size / 2 - 2, 6, Math.PI / 6);
  g.generateTexture(key, size, size);
  g.destroy();
}

function makeMolOxygen(scene: Phaser.Scene, key: string): void {
  const size = 22;
  const g = scene.add.graphics();
  g.fillStyle(COLORS.molOxygen, 1);
  g.lineStyle(2, COLORS.molOxygenOutline, 1);
  g.fillCircle(size / 2, size / 2, size / 2 - 2);
  g.strokeCircle(size / 2, size / 2, size / 2 - 2);
  g.generateTexture(key, size, size);
  g.destroy();
}

function makeMolCo2(scene: Phaser.Scene, key: string): void {
  const size = 24;
  const g = scene.add.graphics();
  g.fillStyle(COLORS.molCo2, 1);
  g.lineStyle(2, COLORS.molCo2Outline, 1);
  drawStar(g, size / 2, size / 2, size / 2 - 1, (size / 2 - 1) * 0.45, 5, -Math.PI / 2);
  g.generateTexture(key, size, size);
  g.destroy();
}

function makeMolBlood(scene: Phaser.Scene, key: string): void {
  const size = 14;
  const g = scene.add.graphics();
  g.fillStyle(COLORS.bloodSpurt, 1);
  g.fillCircle(size / 2, size / 2, size / 2 - 1);
  g.generateTexture(key, size, size);
  g.destroy();
}

// Gruel molecule: an irregular yellow-green blob (distinct from food's hex).
function makeMolGruel(scene: Phaser.Scene, key: string): void {
  const size = 20;
  const g = scene.add.graphics();
  g.fillStyle(COLORS.molGruel, 1);
  g.lineStyle(2, COLORS.molGruelOutline, 1);
  g.beginPath();
  const cx = size / 2,
    cy = size / 2;
  const r = size / 2 - 2;
  const pts = 10;
  for (let i = 0; i < pts; i++) {
    const a = ((Math.PI * 2) / pts) * i;
    const wobble = 0.85 + 0.15 * Math.sin(a * 3);
    const x = cx + r * wobble * Math.cos(a);
    const y = cy + r * wobble * Math.sin(a);
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.closePath();
  g.fillPath();
  g.strokePath();
  g.generateTexture(key, size, size);
  g.destroy();
}

/**
 * Pulse — invisible mechanical-signal molecule (diaphragm → lungs).
 * Never rendered: FlowFx.spawnOne short-circuits for 'pulse', and the
 * direct-link pipe between diaphragm and lungs renders no segments.
 * We still need a texture key for type-completeness of MOL_TEXTURE.
 * Render a 1×1 transparent pixel.
 */
function makeMolPulse(scene: Phaser.Scene, key: string): void {
  const g = scene.add.graphics();
  g.fillStyle(0x000000, 0);
  g.fillRect(0, 0, 1, 1);
  g.generateTexture(key, 1, 1);
  g.destroy();
}

/**
 * Waste molecule — brown wobbly blob, same silhouette as gruel but a
 * different palette so students can tell digestive waste apart from
 * undigested gruel inside the same yellow-green fluid class.
 */
function makeMolWaste(scene: Phaser.Scene, key: string): void {
  const size = 20;
  const g = scene.add.graphics();
  g.fillStyle(COLORS.molWaste, 1);
  g.lineStyle(2, COLORS.molWasteOutline, 1);
  g.beginPath();
  const cx = size / 2,
    cy = size / 2;
  const r = size / 2 - 2;
  const pts = 10;
  // Slightly different wobble pattern from gruel so the silhouette isn't identical.
  for (let i = 0; i < pts; i++) {
    const a = ((Math.PI * 2) / pts) * i;
    const wobble = 0.82 + 0.18 * Math.sin(a * 2 + 0.7);
    const x = cx + r * wobble * Math.cos(a);
    const y = cy + r * wobble * Math.sin(a);
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.closePath();
  g.fillPath();
  g.strokePath();
  g.generateTexture(key, size, size);
  g.destroy();
}

// --- Ports -----------------------------------------------------------------

function makePortBase(scene: Phaser.Scene, key: string): void {
  const size = 80;
  const g = scene.add.graphics();
  g.fillStyle(COLORS.portRim, 1);
  g.fillCircle(size / 2, size / 2, size / 2 - 1);
  g.fillStyle(COLORS.portFace, 1);
  g.fillCircle(size / 2, size / 2, size / 2 - 8);
  g.fillStyle(COLORS.portLens, 1);
  g.fillCircle(size / 2, size / 2, size / 2 - 14);
  g.fillStyle(0x2a2a2a, 1);
  for (let i = 0; i < 8; i++) {
    const a = ((Math.PI * 2) / 8) * i + Math.PI / 8;
    const r = size / 2 - 5;
    g.fillCircle(size / 2 + r * Math.cos(a), size / 2 + r * Math.sin(a), 2.2);
  }
  g.generateTexture(key, size, size);
  g.destroy();
}

function makePortCone(scene: Phaser.Scene, key: string): void {
  const w = 44;
  const h = 56;
  const g = scene.add.graphics();
  g.fillStyle(COLORS.portConeFill, 1);
  g.lineStyle(3, COLORS.portConeOutline, 1);
  g.beginPath();
  g.moveTo(0, 2);
  g.lineTo(w - 2, h / 2 - 5);
  g.lineTo(w - 2, h / 2 + 5);
  g.lineTo(0, h - 2);
  g.closePath();
  g.fillPath();
  g.strokePath();
  g.fillStyle(COLORS.portConeOutline, 1);
  g.fillRect(0, 0, 4, h);
  g.fillStyle(0x222222, 1);
  g.fillCircle(2, 8, 2);
  g.fillCircle(2, h - 8, 2);
  g.generateTexture(key, w, h);
  g.destroy();
}

// Castellated ports — square-wave organ-to-organ joint pieces.
// Spout teeth are at offset x positions from receptacle teeth so they interlock
// when both pieces render at the same cell.

function makeCastellatedSpout(scene: Phaser.Scene, key: string): void {
  const teeth: Array<[number, number]> = [
    [4, 13],
    [21, 30],
    [38, 47],
  ];
  drawCastellatedPiece(scene, key, GRID_SIZE, GRID_SIZE, 16, 18, teeth, 'spout');
}

function makeCastellatedReceptacle(scene: Phaser.Scene, key: string): void {
  const teeth: Array<[number, number]> = [
    [14, 20],
    [31, 37],
  ];
  drawCastellatedPiece(scene, key, GRID_SIZE, GRID_SIZE, 16, 18, teeth, 'receptacle');
}

function drawCastellatedPiece(
  scene: Phaser.Scene,
  key: string,
  w: number,
  h: number,
  bodyH: number,
  toothH: number,
  teeth: Array<[number, number]>,
  kind: 'spout' | 'receptacle',
): void {
  const bodyColor = 0xe8e2d0;
  const outline = 0x4a4030;
  const shadow = 0xb8ad95;
  const g = scene.add.graphics();
  const bodyY = kind === 'spout' ? 0 : h - bodyH;
  g.fillStyle(bodyColor, 1);
  g.fillRect(0, bodyY, w, bodyH);
  g.lineStyle(2, outline, 1);
  g.strokeRect(0, bodyY, w, bodyH);
  g.fillStyle(shadow, 1);
  if (kind === 'spout') g.fillRect(0, bodyY + bodyH - 4, w, 4);
  else g.fillRect(0, bodyY, w, 4);
  const toothY = kind === 'spout' ? bodyH : h - bodyH - toothH;
  g.fillStyle(bodyColor, 1);
  for (const [x1, x2] of teeth) {
    g.fillRect(x1, toothY, x2 - x1, toothH);
    g.strokeRect(x1, toothY, x2 - x1, toothH);
  }
  const boltY = bodyY + bodyH / 2;
  g.fillStyle(outline, 1);
  g.fillCircle(10, boltY, 2);
  g.fillCircle(w - 10, boltY, 2);
  g.generateTexture(key, w, h);
  g.destroy();
}

// --- Burst X ---------------------------------------------------------------

function makeBurstX(scene: Phaser.Scene, key: string): void {
  const size = 36;
  const g = scene.add.graphics();
  g.lineStyle(6, COLORS.burstX, 1);
  g.strokeLineShape(new Phaser.Geom.Line(4, 4, size - 4, size - 4));
  g.strokeLineShape(new Phaser.Geom.Line(size - 4, 4, 4, size - 4));
  g.generateTexture(key, size, size);
  g.destroy();
}

// --- Drawing helpers (shared by molecule textures and organ files) --------

export function drawPolygon(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  r: number,
  sides: number,
  startAngle: number,
): void {
  g.beginPath();
  for (let i = 0; i < sides; i++) {
    const a = startAngle + ((Math.PI * 2) / sides) * i;
    const x = cx + r * Math.cos(a);
    const y = cy + r * Math.sin(a);
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.closePath();
  g.fillPath();
  g.strokePath();
}

export function drawStar(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  outerR: number,
  innerR: number,
  points: number,
  startAngle: number,
): void {
  g.beginPath();
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outerR : innerR;
    const a = startAngle + (Math.PI / points) * i;
    const x = cx + r * Math.cos(a);
    const y = cy + r * Math.sin(a);
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.closePath();
  g.fillPath();
  g.strokePath();
}
